import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { X, Search, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import type { Profile } from '../types/database';
import { cn } from '../lib/utils';

interface FriendSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectFriends: (friends: Profile[]) => void;
  selectedFriends: Profile[];
}

export default function FriendSelector({ 
  isOpen, 
  onClose, 
  onSelectFriends, 
  selectedFriends 
}: FriendSelectorProps) {
  const { user } = useAuth();
  const [friends, setFriends] = useState<Profile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadFriends();
    }
  }, [isOpen, user]);

  const loadFriends = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Get accepted friendships
      const { data: friendships, error: friendError } = await supabase
        .from('friends')
        .select(`
          sender_id,
          receiver_id,
          sender:profiles!friends_sender_id_fkey(id, full_name, avatar_url),
          receiver:profiles!friends_receiver_id_fkey(id, full_name, avatar_url)
        `)
        .eq('status', 'accepted')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);

      if (friendError) throw friendError;

      // Extract friend profiles
      const friendProfiles = friendships?.map(friendship => {
        return friendship.sender_id === user.id ? friendship.receiver : friendship.sender;
      }).filter(Boolean) || [];

      setFriends(friendProfiles);
    } catch (err: any) {
      console.error('Error loading friends:', err);
      setError('Failed to load friends');
    } finally {
      setLoading(false);
    }
  };

  const filteredFriends = friends.filter(friend =>
    friend.full_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleFriendToggle = (friend: Profile) => {
    const isSelected = selectedFriends.some(f => f.id === friend.id);
    
    if (isSelected) {
      onSelectFriends(selectedFriends.filter(f => f.id !== friend.id));
    } else {
      if (selectedFriends.length < 10) { // Limit to 10 tags
        onSelectFriends([...selectedFriends, friend]);
      }
    }
  };

  const getAvatarUrl = (profile: Profile) => {
    if (profile.avatar_url) {
      if (profile.avatar_url.startsWith('http')) {
        return profile.avatar_url;
      }
      const { data } = supabase.storage.from('avatars').getPublicUrl(profile.avatar_url);
      return data.publicUrl;
    }
    return `https://api.dicebear.com/7.x/initials/svg?seed=${profile.full_name}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <Users className="h-5 w-5 text-blue-500" />
            <h3 className="text-lg font-semibold">Tag Friends</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Selected friends */}
        {selectedFriends.length > 0 && (
          <div className="p-4 border-b bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-2">
              Tagged ({selectedFriends.length}/10):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedFriends.map(friend => (
                <div
                  key={friend.id}
                  className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                >
                  <img
                    src={getAvatarUrl(friend)}
                    alt={friend.full_name}
                    className="h-5 w-5 rounded-full object-cover"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${friend.full_name}`;
                    }}
                  />
                  <span>{friend.full_name}</span>
                  <button
                    onClick={() => handleFriendToggle(friend)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search friends..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Friends list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              {error}
            </div>
          ) : filteredFriends.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchQuery ? 'No friends found' : 'No friends to tag'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredFriends.map(friend => {
                const isSelected = selectedFriends.some(f => f.id === friend.id);
                const canSelect = selectedFriends.length < 10 || isSelected;
                
                return (
                  <button
                    key={friend.id}
                    onClick={() => handleFriendToggle(friend)}
                    disabled={!canSelect}
                    className={cn(
                      "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors",
                      isSelected 
                        ? "bg-blue-50 border-2 border-blue-200" 
                        : canSelect
                        ? "hover:bg-gray-50 border-2 border-transparent"
                        : "opacity-50 cursor-not-allowed border-2 border-transparent"
                    )}
                  >
                    <img
                      src={getAvatarUrl(friend)}
                      alt={friend.full_name}
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.src = `https://api.dicebear.com/7.x/initials/svg?seed=${friend.full_name}`;
                      }}
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{friend.full_name}</p>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <X className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}